/**
 * Custom logger utility that handles environment-aware logging
 * Sensitive information is not logged in production environments
 */

// Check if we're in production using both NODE_ENV and Heroku DYNO detection
const isProduction = process.env.NODE_ENV === 'production' || !!process.env.DYNO;

// Log environment detection on initialization (only in non-production)
if (!isProduction) {
  console.log('Logger initialized:');
  console.log('- NODE_ENV:', process.env.NODE_ENV);
  console.log('- Heroku DYNO:', process.env.DYNO ? 'Yes' : 'No');
  console.log('- Production mode:', isProduction ? 'Yes' : 'No');
}

// Define sensitive keys that should never be logged in production
const sensitiveKeys = [
  'password', 
  'token', 
  'authToken', 
  'authorization',
  'auth',
  'api_key',
  'apiKey',
  'email',
  'phone',
  'username',
  'credential',
  'secret'
];

/**
 * Sanitize objects to remove sensitive data before logging
 * @param {any} data - Data to sanitize
 * @return {any} - Sanitized data
 */
const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  // Handle array case
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }

  // Handle object case
  const sanitized = { ...data };
  Object.keys(sanitized).forEach(key => {
    const lowerKey = key.toLowerCase();
    
    // Check if this key contains any sensitive terms
    if (sensitiveKeys.some(term => lowerKey.includes(term))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
      // Recursively sanitize nested objects
      sanitized[key] = sanitizeData(sanitized[key]);
    }
  });

  return sanitized;
};

const logger = {
  /**
   * Log information messages
   * @param {...any} args - Arguments to log
   */
  info: (...args) => {
    if (isProduction) {
      // In production, sanitize the data before logging
      const sanitizedArgs = args.map(arg => 
        typeof arg === 'object' && arg !== null ? sanitizeData(arg) : arg
      );
      console.log(...sanitizedArgs);
    } else {
      // In development, log everything
      console.log(...args);
    }
  },

  /**
   * Log warning messages
   * @param {...any} args - Arguments to log
   */
  warn: (...args) => {
    if (isProduction) {
      const sanitizedArgs = args.map(arg => 
        typeof arg === 'object' && arg !== null ? sanitizeData(arg) : arg
      );
      console.warn(...sanitizedArgs);
    } else {
      console.warn(...args);
    }
  },

  /**
   * Log error messages (always logged in all environments but still sanitized)
   * @param {...any} args - Arguments to log
   */
  error: (...args) => {
    if (isProduction) {
      const sanitizedArgs = args.map(arg => 
        typeof arg === 'object' && arg !== null ? sanitizeData(arg) : arg
      );
      console.error(...sanitizedArgs);
    } else {
      console.error(...args);
    }
  },

  /**
   * Debug logging - only in development, never in production
   * @param {...any} args - Arguments to log
   */
  debug: (...args) => {
    if (!isProduction) {
      console.log('[DEBUG]', ...args);
    }
    // In production, debug logs are completely ignored
  }
};

export default logger; 